import { useState, type JSX } from 'react'
import { Share2 } from 'lucide-react'
import type { Capability } from '@shared/collaboration/access'
import { ConfigService } from '@/services/ConfigService'
import { hasCapability } from '../hasCapability'
import { ShareDialog } from './ShareDialog'
import type { ResourceType } from '../api/use-grants'

interface ShareButtonProps {
  resourceType: ResourceType
  resourceId: string
  resourceName: string
  capabilities?: Capability[] | undefined
}

/**
 * Icon button that opens the "who has access" ShareDialog for a project or
 * model. Fail-closed: renders nothing unless the `collaboration` feature
 * flag is on AND the caller's capabilities include `share`. Both gates must
 * pass — an undefined `capabilities` (e.g. dashboard cards, whose endpoint
 * doesn't resolve access) hides the button rather than guessing.
 */
export function ShareButton({
  resourceType,
  resourceId,
  resourceName,
  capabilities,
}: ShareButtonProps): JSX.Element | null {
  const [open, setOpen] = useState(false)

  // The `!capabilities` check both fail-closes on an undefined resource
  // (dashboard cards, whose payload never resolves access) and narrows
  // `capabilities` to a definite array for the JSX below.
  if (!capabilities || !ConfigService.isFeatureEnabled('collaboration') || !hasCapability({ capabilities }, 'share')) {
    return null
  }

  return (
    <>
      <button
        type="button"
        aria-label="Share"
        title="Share"
        onClick={(e): void => {
          // These buttons are rendered inside <Link>-wrapped cards in
          // several placements; stop the click from also navigating.
          e.preventDefault()
          e.stopPropagation()
          setOpen(true)
        }}
        className="rounded-md p-1 text-muted-foreground/50 transition-colors hover:bg-primary/10 hover:text-primary"
      >
        <Share2 className="h-3.5 w-3.5" />
      </button>
      <ShareDialog
        open={open}
        onOpenChange={setOpen}
        resourceType={resourceType}
        resourceId={resourceId}
        resourceName={resourceName}
        capabilities={capabilities}
      />
    </>
  )
}
